import { BringAgentDialog } from "@app/components/assistant/BringAgentDialog";
import { ImportSkillsDialog } from "@app/components/skills/import/ImportSkillsDialog";
import { useYAMLUpload } from "@app/hooks/useYAMLUpload";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useAppRouter } from "@app/lib/platform";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { TRACKING_AREAS, withTracking } from "@app/lib/tracking";
import {
  getAgentBuilderRoute,
  getRoutinesRoute,
  getSkillBuilderRoute,
} from "@app/lib/utils/router";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  File02,
  FolderOpen,
  Plus,
  PuzzlePiece01,
  Robot,
  Spinner,
  Users01,
} from "@dust-tt/sparkle";
import type { ComponentProps, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

interface CreateDropdownProps {
  owner: LightWorkspaceType;
  dataGtmLocation: string;
  variant?: ComponentProps<typeof Button>["variant"];
  className?: string;
  label?: string;
  scope?: "all" | "agents" | "skills";
  size?: ComponentProps<typeof Button>["size"];
  trigger?: ReactNode;
  openOnHover?: boolean;
  children?: ReactNode;
}

export const createMenuContentClasses =
  "w-60 min-w-56 rounded-xl border border-stone-300 bg-white p-1.5 shadow-2xl shadow-stone-900/10 dark:border-white/50 dark:bg-[#0a0a0a] dark:shadow-2xl dark:shadow-black/90";

export const createMenuItemClasses = cn(
  "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 cursor-pointer",
  "text-stone-900 dark:text-white",
  "border border-transparent bg-transparent",
  "hover:bg-stone-100 hover:border-stone-300 dark:hover:bg-white/[0.08] dark:hover:border-white/25 dark:hover:text-white",
  "data-[highlighted]:bg-stone-100 data-[highlighted]:border-stone-300 dark:data-[highlighted]:bg-white/[0.08] dark:data-[highlighted]:border-white/25 dark:data-[highlighted]:text-white",
  "[&_svg]:text-stone-700 hover:[&_svg]:text-stone-950 dark:[&_svg]:text-stone-300 dark:hover:[&_svg]:text-white data-[highlighted]:[&_svg]:text-stone-950 dark:data-[highlighted]:[&_svg]:text-white",
  "data-[disabled]:opacity-40 data-[disabled]:pointer-events-none",
  "mb-0.5 last:mb-0"
);

const hoverCloseDelayMs = 150;

const sectionDividerClasses =
  "my-1.5 border-t border-stone-200 dark:border-white/10";

const labelClasses =
  "px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400";

export const CreateDropdown = ({
  owner,
  dataGtmLocation,
  variant = "primary",
  className,
  label = "Create",
  scope = "all",
  size = "sm",
  trigger,
  openOnHover = false,
  children,
}: CreateDropdownProps) => {
  const router = useAppRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isImportSkillDialogOpen, setIsImportSkillDialogOpen] = useState(false);
  const [isBringAgentDialogOpen, setIsBringAgentDialogOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const hoverProps = openOnHover
    ? {
        onPointerEnter: () => {
          clearTimeout(closeTimer.current);
          setIsMenuOpen(true);
        },
        onPointerLeave: () => {
          clearTimeout(closeTimer.current);
          closeTimer.current = setTimeout(
            () => setIsMenuOpen(false),
            hoverCloseDelayMs
          );
        },
      }
    : {};
  const { isUploading: isUploadingYAML, triggerYAMLUpload } = useYAMLUpload({
    owner,
  });
  const { hasPermission } = useWorkspacePermissions();
  const { hasFeature } = useFeatureFlags();

  const canCreateAgent =
    (scope === "all" || scope === "agents") && hasPermission("create", "agent");
  const canCreateSkill =
    (scope === "all" || scope === "skills") && hasPermission("create", "skill");
  const canBringAgent = canCreateAgent && hasFeature("byoa_platform");

  // The section labels only disambiguate when more than one group is listed.
  const showSectionLabels =
    [children, canCreateAgent, canCreateSkill].filter(Boolean).length > 1;

  // Each group is gated on its own permission, so the button itself is only
  // worth showing when there is at least one thing the user can create.
  if (!canCreateAgent && !canCreateSkill && !children) {
    return null;
  }

  return (
    <>
      <DropdownMenu
        modal={!openOnHover}
        open={isMenuOpen}
        onOpenChange={(open) => {
          clearTimeout(closeTimer.current);
          setIsMenuOpen(open);
        }}
      >
        <DropdownMenuTrigger
          asChild
          {...hoverProps}
          onPointerDown={(event) => {
            // Hover already opened the menu; a click must not toggle it shut.
            if (openOnHover && isMenuOpen) {
              event.preventDefault();
            }
          }}
        >
          {trigger ?? (
            <Button
              variant={variant}
              icon={Plus}
              label={label}
              data-gtm-label="assistantCreationButton"
              data-gtm-location={dataGtmLocation}
              onClick={withTracking(TRACKING_AREAS.BUILDER, "create_menu")}
              size={size}
              isSelect
              isLoading={isLoading}
              disabled={isLoading}
              className={className}
            />
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className={createMenuContentClasses}
          {...hoverProps}
        >
          {children}
          {canCreateAgent && (
            <>
              {showSectionLabels && (
                <>
                  {children && <div className={sectionDividerClasses} />}
                  <DropdownMenuLabel label="Agents" className={labelClasses} />
                </>
              )}
              <DropdownMenuItem
                label="Agent from template"
                icon={Users01}
                className={createMenuItemClasses}
                onClick={withTracking(
                  TRACKING_AREAS.BUILDER,
                  "create_from_template",
                  () => void router.push(getRoutinesRoute(owner.sId))
                )}
              />
              <DropdownMenuItem
                label="Agent from scratch"
                icon={File02}
                className={createMenuItemClasses}
                onClick={withTracking(
                  TRACKING_AREAS.BUILDER,
                  "create_from_scratch",
                  () => {
                    setIsLoading(true);
                    void router.push(getAgentBuilderRoute(owner.sId, "new"));
                  }
                )}
              />
              <DropdownMenuItem
                label={isUploadingYAML ? "Uploading..." : "Agent from YAML"}
                icon={isUploadingYAML ? <Spinner size="xs" /> : FolderOpen}
                className={createMenuItemClasses}
                disabled={isUploadingYAML}
                onClick={triggerYAMLUpload}
              />
              {canBringAgent && (
                <DropdownMenuItem
                  label="Bring Agent"
                  icon={Robot}
                  className={createMenuItemClasses}
                  onClick={() => setIsBringAgentDialogOpen(true)}
                />
              )}
            </>
          )}
          {canCreateSkill && (
            <>
              {showSectionLabels && (
                <>
                  {(children || canCreateAgent) && (
                    <div className={sectionDividerClasses} />
                  )}
                  <DropdownMenuLabel label="Skills" className={labelClasses} />
                </>
              )}
              <DropdownMenuItem
                label="Skill from scratch"
                icon={PuzzlePiece01}
                className={createMenuItemClasses}
                onClick={withTracking(
                  TRACKING_AREAS.BUILDER,
                  "create_skill_from_scratch",
                  () => {
                    setIsLoading(true);
                    void router.push(getSkillBuilderRoute(owner.sId, "new"));
                  }
                )}
              />
              <DropdownMenuItem
                label="Skill from existing"
                icon={FolderOpen}
                className={createMenuItemClasses}
                onClick={() => setIsImportSkillDialogOpen(true)}
              />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isImportSkillDialogOpen && (
        <ImportSkillsDialog
          onClose={() => setIsImportSkillDialogOpen(false)}
          owner={owner}
        />
      )}
      <BringAgentDialog
        owner={owner}
        isOpen={isBringAgentDialogOpen}
        onClose={() => setIsBringAgentDialogOpen(false)}
      />
    </>
  );
};
